/** Base declaration; the pure composer supplies the adapter target schema and extension effects. */
export const scenePlanSchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: 'https://schemas.toolproof.com/v1/ScenePlan.json',
    title: 'ScenePlan',
    type: 'object',
    properties: {
        schemaVersion: { const: 1 },
        title: { type: 'string', minLength: 1, maxLength: 12000 },
        trace: { $ref: '#/$defs/trace' },
        beats: { type: 'array', items: { $ref: '#/$defs/beat' }, minItems: 1, maxItems: 24 },
    },
    required: ['schemaVersion', 'title', 'trace', 'beats'],
    additionalProperties: false,
    $defs: {
        trace: {
            type: 'object',
            properties: {
                adapter: { type: 'string', minLength: 1, maxLength: 12000 },
                traceKind: { type: 'string', minLength: 1, maxLength: 12000 },
                handle: { type: 'string', minLength: 1, maxLength: 12000 },
                schemaVersion: { type: 'integer', minimum: 1 },
            },
            required: ['adapter', 'traceKind', 'handle', 'schemaVersion'],
            additionalProperties: false,
        },
        anchor: {
            type: 'object',
            properties: {
                landmark: { type: 'string', minLength: 1, maxLength: 12000 },
                phase: { enum: ['start', 'complete'] },
            },
            required: ['landmark'],
            additionalProperties: false,
        },
        target: {},
        effect: { oneOf: [
                {
                    type: 'object',
                    properties: {
                        effectKind: { const: 'emphasis' },
                        target: { $ref: '#/$defs/target' },
                        caption: { type: 'string', minLength: 1, maxLength: 60 },
                        marks: { type: 'array', minItems: 1, uniqueItems: true,
                            items: { enum: ['border', 'contour', 'pointer', 'spotlight'] } },
                    },
                    required: ['effectKind', 'target'],
                    additionalProperties: false,
                    if: { properties: { caption: {}, marks: {} }, required: ['caption', 'marks'] },
                    then: { properties: { marks: { type: 'array', contains: { const: 'pointer' } } } },
                },
                {
                    type: 'object',
                    properties: {
                        effectKind: { const: 'orbit' },
                        target: { $ref: '#/$defs/target' },
                        durationMs: { type: 'number', exclusiveMinimum: 0, maximum: 60000 },
                        angleRadians: { type: 'number', minimum: -3.141592653589793, maximum: 3.141592653589793, not: { const: 0 } },
                    },
                    required: ['effectKind', 'target', 'durationMs', 'angleRadians'],
                    additionalProperties: false,
                },
            ] },
        cue: {
            type: 'object',
            properties: {
                id: { type: 'string', pattern: '^[a-z][a-z0-9-]*$', maxLength: 100 },
                beforeText: { type: 'string', minLength: 1, maxLength: 12000 },
                landmark: { type: 'string', minLength: 1, maxLength: 12000 },
                phase: { enum: ['start', 'complete'] },
            },
            required: ['id', 'beforeText', 'landmark'],
            additionalProperties: false,
        },
        pointerPlacement: {
            type: 'object',
            properties: {
                focus: { enum: ['stage', 'targets'] },
                depth: { type: 'number', minimum: 0, maximum: 0.9 },
                lift: { type: 'number', minimum: 0, maximum: 4 },
            },
            required: ['focus', 'depth', 'lift'],
            additionalProperties: false,
        },
        textPanel: {
            type: 'object',
            properties: {
                heading: { type: 'string', minLength: 1, maxLength: 100, pattern: '\\S' },
                groups: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            heading: { type: 'string', minLength: 1, maxLength: 100, pattern: '\\S' },
                            items: { type: 'array',
                                items: { type: 'string', minLength: 1, maxLength: 240, pattern: '\\S' },
                                minItems: 1, maxItems: 6 },
                        },
                        required: ['heading', 'items'],
                        additionalProperties: false,
                    },
                    minItems: 1, maxItems: 3,
                },
                note: { type: 'string', minLength: 1, maxLength: 240, pattern: '\\S' },
            },
            required: ['heading', 'groups'],
            additionalProperties: false,
        },
        presentation: {
            type: 'object',
            properties: {
                id: { type: 'string', pattern: '^[a-z][a-z0-9-]*$', maxLength: 100 },
                afterCue: { type: 'string', pattern: '^[a-z][a-z0-9-]*$', maxLength: 100 },
                until: { $ref: '#/$defs/anchor' },
                cameraView: { type: 'string', minLength: 1, maxLength: 12000, pattern: '\\S' },
                pointerPlacement: { $ref: '#/$defs/pointerPlacement' },
                textPanel: { $ref: '#/$defs/textPanel' },
                effects: { type: 'array', items: { $ref: '#/$defs/effect' }, minItems: 0, maxItems: 8 },
            },
            required: ['id', 'effects'],
            additionalProperties: false,
        },
        beat: {
            type: 'object',
            properties: {
                id: { type: 'string', pattern: '^[a-z][a-z0-9-]*$', maxLength: 100 },
                landmark: { type: 'string', minLength: 1, maxLength: 12000 },
                replayFrom: { $ref: '#/$defs/anchor' },
                explanation: { type: 'string', minLength: 1, maxLength: 12000 },
                spokenText: { type: 'string', minLength: 1, maxLength: 12000 },
                narrationTextMap: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            spokenOffset: { type: 'integer', minimum: 0 },
                            displayOffset: { type: 'integer', minimum: 0 },
                        },
                        required: ['spokenOffset', 'displayOffset'],
                        additionalProperties: false,
                    },
                    minItems: 2, maxItems: 256,
                },
                narrationPauses: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            offset: { type: 'integer', minimum: 1 },
                            milliseconds: { type: 'integer', minimum: 1, maximum: 10000 },
                        },
                        required: ['offset', 'milliseconds'],
                        additionalProperties: false,
                    },
                    minItems: 0, maxItems: 64,
                },
                readingHoldMs: { type: 'number', minimum: 0, maximum: 120000 },
                cues: { type: 'array', items: { $ref: '#/$defs/cue' }, minItems: 0, maxItems: 16 },
                presentation: { type: 'array', items: { $ref: '#/$defs/presentation' }, minItems: 1, maxItems: 17 },
            },
            required: ['id', 'landmark', 'explanation', 'readingHoldMs'],
            additionalProperties: false,
        },
    },
};
